import * as T from '@radix-ui/react-tabs';
import { type ComponentPropsWithoutRef, createContext, type MouseEvent, useContext, useRef, useState } from 'react';

import './ui.css';

const cx = (base: string, extra: string | undefined) => (extra ? `${base} ${extra}` : base);

/**
 * Views that swap the panel under them (Nest's Today / This week / All time, the level
 * cards): Radix Tabs, so a tablist with one Tab stop, the arrows moving between tabs and
 * showing each, and each panel named by its tab. The list prints as the segmented control
 * (ui.css `.seg`) unless given its own class.
 *
 *   <Tabs value={view} onValueChange={setView}>
 *     <TabsList aria-label="Show"><TabsTrigger value="today">Today</TabsTrigger>…</TabsList>
 *     <TabsContent value="today">…</TabsContent>
 *   </Tabs>
 */
export function Tabs({ value, defaultValue, onValueChange, ...props }: ComponentPropsWithoutRef<typeof T.Root>) {
	const [own, setOwn] = useState(defaultValue);
	const shown = value ?? own;
	// The value as last set, so a click straight after the mousedown that chose it doesn't choose it twice.
	const latest = useRef(shown);
	latest.current = shown;
	const set = (next: string) => {
		if (next === latest.current) return;
		latest.current = next;
		if (value === undefined) setOwn(next);
		onValueChange?.(next);
	};
	return (
		<Chosen.Provider value={set}>
			<T.Root {...props} value={shown} onValueChange={set} />
		</Chosen.Provider>
	);
}

const Chosen = createContext<((value: string) => void) | null>(null);

/** The row of tabs. Name it (`aria-label` or `aria-labelledby`). `className` replaces the `.seg` look. */
export function TabsList({ className, ...props }: ComponentPropsWithoutRef<typeof T.List>) {
	return <T.List className={className ?? 'seg'} {...props} />;
}

/**
 * One tab. `className` replaces the `.seg-opt` look. Radix chooses a tab on mousedown or
 * focus; a plain click (a script, some assistive tech, voice control) chooses it too.
 */
export function TabsTrigger({ className, onClick, ...props }: ComponentPropsWithoutRef<typeof T.Trigger>) {
	const choose = useContext(Chosen);
	return (
		<T.Trigger
			className={className ?? 'seg-opt'}
			{...props}
			onClick={(e: MouseEvent<HTMLButtonElement>) => {
				onClick?.(e);
				if (!e.defaultPrevented && !props.disabled) choose?.(props.value);
			}}
		/>
	);
}

/** A tab's panel, named by its tab. */
export function TabsContent({ className, ...props }: ComponentPropsWithoutRef<typeof T.Content>) {
	return <T.Content className={cx('ui-tabpanel', className)} {...props} />;
}
